import { useState, type FormEvent } from 'react';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import FormLabel from '@/components/auth/FormLabel';
import FormLayout from '@/components/auth/FormLayout';

type RequestStatus = 'idle' | 'sent';

export default function PasswordResetRequestTemplate() {
  const [status, setStatus] = useState<RequestStatus>('idle');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendResetMail = async (targetEmail: string) => {
    const normalizedEmail = targetEmail.trim();
    if (!normalizedEmail) {
      setError('メールアドレスを入力してください');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)
        ?.content;
      const response = await fetch('/account/api/password_resets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken && { 'X-CSRF-Token': csrfToken }),
        },
        body: JSON.stringify({ email: normalizedEmail }),
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'メールの送信に失敗しました');
        return;
      }

      setEmail(normalizedEmail);
      setStatus('sent');
    } catch {
      setError('通信エラーが発生しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await sendResetMail(email);
  };

  const handleResend = async () => {
    await sendResetMail(email);
  };

  return (
    <FormLayout>
      <div className="flex size-full flex-col items-center justify-center">
        <div className="mb-8 text-center">
          <img src="/agent/product-logo.png" alt="Agent Hub Logo" className="h-10 w-auto" />
        </div>

        <div className="w-full max-w-[580px] rounded-xl p-8">
          {status === 'idle' ? (
            <>
              <h1 className="text-center text-2xl leading-tight font-bold text-black">
                パスワードをリセットする
              </h1>
              <p className="mt-6 text-center text-sm leading-6 text-black/70">
                「パスワード再設定ページのURL」を、登録メールアドレスに送信します。
                <br />
                ご登録時のメールアドレスを入力して「送信する」ボタンを押してください。
              </p>

              {error && (
                <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </p>
              )}

              <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                <div>
                  <FormLabel htmlFor="password-reset-email">メールアドレス</FormLabel>
                  <Input
                    id="password-reset-email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="メールアドレス"
                    className="mt-2 w-full"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  color="primary"
                  size="lg"
                  full
                  disabled={isSubmitting || !email.trim()}
                >
                  {isSubmitting ? '送信中...' : '送信する'}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h1 className="text-center text-2xl leading-tight font-bold text-black">
                メールを送信しました
              </h1>
              <p className="mt-6 text-center text-sm leading-6 text-black/70">
                パスワードリセットに必要なメールを送信しました。6時間以内にメールに記載されたリンクをクリックし、パスワードの再設定に進んでください。
              </p>
              <p className="mt-4 text-center text-sm leading-6 text-black/70">
                メールが届かない場合や紛失した場合は、以下より再送信してください。
              </p>

              {error && (
                <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </p>
              )}

              <div className="mt-8">
                <Button
                  type="button"
                  color="primary"
                  size="lg"
                  full
                  disabled={isSubmitting}
                  onClick={handleResend}
                >
                  {isSubmitting ? '再送信中...' : 'メールを再送信する'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </FormLayout>
  );
}
